/**
 * №29 (D-L1, S0) — THE LEFT ZONE: navigation, on every route, always.
 *
 * The locked probe (`docs/mockups/legora-inspired-probe/index.html`, mockups A–E)
 * puts navigation in one full-height column with the brand at its top — there is no
 * top bar. This component is that column. It replaces the №14.1 `.topbar` + `.rail`
 * pair: the identity lockup moves to the head of this column, and the affordances
 * that lived on the right of the top bar (workspace switcher, theme, Console,
 * the signed-in avatar) move to its foot, where they read as workspace controls
 * rather than page chrome.
 *
 * ── №53 (53.0, D-CR12) — THE RAIL IS SIX ROWS AND NOTHING ELSE ───────────────
 *
 *   brand · SEARCH · the six · clock · you
 *
 * David, 2026-08-19: *"I do not like the Records & Equipment… Could we put all of
 * our record lists here in the same place you could just search for them?"* So
 * every record list left the rail and ⌘K carries them (it already indexed every
 * row of every Place-backed type — №44). The rail now renders only the Places the
 * SERVER marks `section: "day"`: four shell-owned rows and two module
 * contributions (D-CR20), never a hardcoded list of six.
 *
 * **What left, and where it went — nothing is lost, and none of it is a dead end:**
 *   · Leads · Opportunities · Contacts · Clients · Registers · Invoices …
 *          → ⌘K's `Records` group. Still assembled, still install-gated, still
 *            area-projected; simply not rail rows.
 *   · the {collective} roster → ⌘K's roster commands today, and Workforce (53.2),
 *          which is the surface a staff room actually deserves.
 *   · RUNNING → Today (53.5), whose whole subject is what is happening now. This
 *          is the one real loss in this rung and it is temporary: until 53.5 the
 *          shell has no live-run glance. Recorded rather than quietly dropped.
 *
 * The rail cannot lose the record rows before the palette is a real way to them,
 * which is why the ⌘K work and this deletion are ONE rung and not two.
 *
 * **The probe's missing rows — where S2 left them (Q2).** S0 shipped none of
 * `New…` · `Search` · `Library`, the OPEN section, or VIEWS, because each needs a
 * subject to open and this layer bans dead nav. S2 built the subjects, so the rows
 * that now GO somewhere ship, and the ones that still don't, still don't:
 *
 *   New…    ❌ REMOVED in №22.6 — the row shipped in S2 because the route resolved,
 *                     but mockup C's draft-record payoff was never built, so it
 *                     promised creation and delivered a chat box on an empty page.
 *                     Ruled out by David. The route stays; the row returns with the
 *                     propose-then-commit contract (D-L3), not before.
 *   OPEN    ✅ ships — the record you have open, with the sub-views the MATRIX
 *                     declares for it (`openRecordNav`). Generated, not hand-kept:
 *                     every row is a registered route the coverage gate checks, so
 *                     the section cannot grow a row that goes nowhere.
 *   Search  ❌ absent — the App Shell has no search surface. The command palette is
 *                     console-only, and building an `/app` search is a surface, not
 *                     a nav row. Still out, still recorded.
 *   Library ❌ absent — a maker/console surface, out of scope per the spec's §7.
 *   VIEWS   ❌ absent — see the S2 findings: the recorded firm has exactly ONE
 *                     declared view per Place (the default list), there is no way to
 *                     create a second from `/app`, and a section listing the view you
 *                     are already looking at plus a `＋ New view` that cannot create
 *                     one is two dead rows wearing one heading.
 *
 * The rule that decided each of those is the same one, unchanged since S0: a row
 * that goes nowhere does not ship.
 *
 * **RUNNING is real or absent.** The rows come from `get_workforce_status`, which
 * derives liveness from a run's own evidence (D-ST5 / №22.4.1 F1 — "live means
 * executing now"). No liveness is simulated, and when nothing is running the
 * section does not render at all rather than showing an empty frame.
 *
 * D-L6 vocabulary: the roster heading is the workspace's own word for its people
 * (Workforce / Crew / Team …). Nothing here says "agent".
 */
import { NavLink, useLocation } from "react-router";
import { useShellIdentity } from "../../hooks/use-shell-identity";
import { ShellProfileMenu } from "./ShellProfileMenu";
import { GhostGridK } from "../../brand/GhostGridK";
import { workbenchNavRoute } from "./workbench-registry";
import { useNeedsYouGlanceCount } from "../../hooks/use-needs-you-count";
import type { SitemapEntry } from "./sitemap";

/* №53 (53.0, D-CR9) — THE RAIL ROWS CARRY NO ICONS.
 *
 * `GLYPH_PATHS` (a 12-entry hand-drawn SVG table) and the `<Glyph>` component are
 * DELETED. The approved mockups draw the rail as label + count and nothing else —
 * `crew.css`'s `.nav button` is a flex row with `justify-content: space-between`
 * holding a word and a pill — and with six rows there is nothing for an icon to
 * disambiguate. The icons were carried verbatim out of the retired `.rail` at
 * №14.1.1-B, where the rail held eleven rows and needed them.
 *
 * The sitemap still CARRIES `glyph` and the server still sets it: it is consumed
 * by ⌘K's record rows, where a list of forty results does need a shape. Only the
 * rail stopped drawing them.
 */

/**
 * №48 — is `pathname` a RECORD inside this Place? Only ever true for a Place of
 * records (`/app/place/<entity>`), and only at a segment boundary, so the
 * catch-all Place whose target prefixes every route can never claim a surface
 * that is not its own.
 */
function isRecordChild(target: string, pathname: string): boolean {
  if (!target.startsWith("/app/place/")) return false;
  return pathname.startsWith(`${target}/`);
}

export function ShellNav({
  brandName,
  places,
  onOpenPalette,
}: {
  brandName: string | null;
  places: SitemapEntry[];
  /** Opens ⌘K. The rail owns the AFFORDANCE (D-CR13); the shell owns the state. */
  onOpenPalette?: () => void;
}) {
  const location = useLocation();
  useShellIdentity("nav");

  // №29 — the "Needs you" row's badge, and since §3 ② deleted the bottom bar, the shell's
  // ONLY live count. RC1-3: **a glance line counts the full actionable set, once.**
  //
  // It counts what the row OPENS, and getting there took two passes worth recording,
  // because the second one is the reason this is a defect CLASS and not a typo:
  //
  //  · S3 found it counting pending authorisations ALONE, so a firm with three unanswered
  //    cards and no authorisation saw a row named "Needs you" wearing no badge at all.
  //    Fixed to authorisations + needs-you cards.
  //  · S4's class gate then caught that fix STILL under-counting. `/app` presents three
  //    groups — decisions, approvals, waits — and "decisions" is escalations PLUS
  //    workflow reviews (`useWorkspaceNeedsYou`, everything not a `wait`). Adding the two
  //    obvious halves looked complete and was not.
  //
  // №53 · 53.0.1 — the derivation itself moved to `useNeedsYouGlanceCount()`
  // (`hooks/use-needs-you-count.ts`): the strip's "need you" tile (D-CR2) needs the
  // SAME number, and a second inline copy is exactly how the class above recurs a
  // third time. Every part still reads the SAME cache its section reads, so
  // answering anything anywhere clears it in lock-step everywhere (D-CC9).
  //
  // №53 — the PER-MEMBER derivations that stood beside this (`byMember`,
  // `byMemberNeedsYou`, `running`, and the whole `useWorkforceStatus` subscription)
  // are DELETED with the roster and RUNNING sections they fed. They were the rail's
  // only reason to poll workforce status; leaving them would have kept a per-hire
  // poll running on every route to populate nothing.
  const needsYouCount = useNeedsYouGlanceCount();

  return (
    <nav className="shellnav" data-testid="shellnav" aria-label="Navigation">
      {/* brand — the ONE identity slot (D-DS4), now at the head of the nav column. */}
      <div className="shellnav__brand">
        <GhostGridK />
        {(() => {
          const words = (brandName ?? "Kuvio").trim().split(/\s+/);
          const top = words[0];
          const bottom = words.slice(1).join(" ");
          return (
            <span className="brand__name">
              <span className="b1">{top}</span>
              {bottom && <span className="b2">{bottom}</span>}
            </span>
          );
        })()}
      </div>

      {/* №53 (D-CR13) — THE AFFORDANCE. Placement A of the three drawn; David:
          *"A (you rec) is the best."* It reads the way every tool a lawyer already
          uses reads — search first, then the places — and it STATES THE SHORTCUT,
          which is the only way anyone ever learns it.

          This is not an input. It is a button shaped like one: typing happens in
          the palette, and a real field here would be a second search box that has
          to be kept in sync with the one that does the work. ⌘K was bound and
          indexed before this rung (№44) and had no affordance at all, so the
          feature was invisible to anyone who did not already know the chord. */}
      <button className="shellsearch" onClick={onOpenPalette} data-testid="shell-search">
        {/* NO magnifier. The approved mockups draw this row as label + ⌘K and
            nothing else (`command-palette.html` frame 1, `profile-and-settings.html`
            frame 1), and in a 184px rail the icon's 22px was the difference between
            "Search or jump to…" reading in full and truncating to "Search or jum…".
            The palette's own input keeps its icon — it has the width for it. */}
        <span className="shellsearch__label">Search or jump to…</span>
        <span className="shellsearch__kbd">⌘K</span>
      </button>

      <div className="shellnav__scroll">
        {/* №22.6 — `New…` IS OUT. David: "I ruled + New was fucked because it did
            nothing."
            It passed the dead-nav ban on a technicality: the route resolves, so the
            row went somewhere. But mockup C's payoff — a DRAFT record you can accept,
            captioned "nothing is saved until you press the button" — was never built
            (it needs a propose-then-commit verb; Kuvio's create verbs write on call).
            So the row promised creation and delivered an empty page with a chat box.
            That is the ban's neighbour and it deserves the same answer: **a row that
            promises what its destination cannot do does not ship.**
            The ROUTE stays (`/app/new`, registered, gated, reachable by URL) — the
            geometry and the subject are real work and the row is one line to restore
            the day the draft card lands. Deleted, not hidden: removals are
            deliverables. See docs/22.6-findings.md F26. */}

        {/* №53 (D-CR12/D-CR20) — the six. No "Practice" heading: a group label over
            the only group on the rail names nothing, and the rail is now one list.
            The filter is the WHOLE mechanism — the shell renders what the server
            marked, so a second vertical gets this rail with its own noun and a
            workspace without `commercial-spine` correctly has no close ritual.
            The rail grows a row when a rung lands its surface, never before. */}
        {places.filter((e) => e.section === "day").map((e) => {
          const badge = e.badgeSource === "needs_you" && needsYouCount > 0 ? needsYouCount : null;
          const target = e.route ?? (e.workbench ? workbenchNavRoute(e.workbench) : undefined);
          // №29 (§3 ⑥, S3) — a Place with no destination renders NOTHING. It used to
          // render a greyed `<button disabled>`, which is the dead-nav ban's exact
          // shape: a row you can see, read and click at, that goes nowhere. Two things
          // made it worth deleting rather than leaving harmless — the `disabled` flag
          // it keyed off is declared by the sitemap and set by nothing (the "uninstalled
          // modules are greyed" behaviour its comment described was never built), and
          // the `!target` half would have greyed a Place whose route simply failed to
          // resolve. A row ships when the subject it opens exists; otherwise it is out,
          // and findings say why.
          if (!target) return null;
          return (
            // №48 — a Place stays current while you are INSIDE one of its records.
            //
            // Deleting the OPEN section removed the one thing that said where you
            // were once you drilled in, so inside a matter the whole rail went dark.
            // The evidence spec caught it by diffing the two rails.
            //
            // The fix is NOT dropping `end`: the Places list includes a catch-all
            // whose target is a prefix of every app route, and without `end` that one
            // lights on every surface — which the same spec then caught too, as
            // "Needs you" being current on a matter list. So `end` stays for the
            // exact match, and record-child is computed EXPLICITLY, only for a Place
            // that is actually a Place-of-records (`/app/place/<entity>`), matched at
            // a segment boundary so `/app/place/matter` cannot claim
            // `/app/place/matters`.
            //
            // This is the distinction the ruling draws: the rail's SHAPE is unaffected
            // by the record (same rows, same order, nothing added or removed), while
            // its you-are-here marker keeps answering "where am I".
            <NavLink
              key={e.label}
              to={target}
              end
              className={({ isActive }) =>
                `navitem${isActive || isRecordChild(target, location.pathname) ? " active" : ""}`
              }
            >
              <span className="navitem__label">{e.label}</span>
              {badge != null ? <span className="count">{badge}</span> : null}
            </NavLink>
          );
        })}

        {/* №48 — the OPEN section is DELETED, not hidden. David, 2026-08-17:
            "Keep navigation unaffected by the current record and do not list the
            current record AT ALL. This is a walkback of the entire idea."

            It rendered the open record plus the sub-views its type declares, and it
            is what E-7 spent a design loop trying to make fit — the measurements are
            in `docs/23-nav-options/`: on a matter the rail needed 841px of 744px, so
            `Program` was clipped and the roster was pushed off-screen. Three options
            were drawn to buy that room back; the ruling was to stop buying it and
            give the space up. The sub-views now render as tabs ON the record
            (`RecordTabs.tsx`), which reverses the PLACEMENT half of №29 §3 ④ and
            leaves its declaration seam (`record-subviews.ts` → `openRecordNav`)
            exactly where it was.

            Removals are deliverables: the rail is now IDENTICAL on every surface,
            which is the property the ruling actually asked for. */}

        {/* №53 (D-CR12) — the {collective} roster and RUNNING sections are DELETED
            from the rail. "Six rows and nothing else" is the ruling, and both were
            rows-and-something-else.

            Neither is lost, and this is the part worth checking rather than
            trusting: the roster is in ⌘K (`app-commands.ts` builds a command per
            hire from the live roster) and gets its own surface in 53.2. RUNNING
            has NO replacement until Today lands in 53.5 — the shell is without a
            live-run glance for those two rungs. That is a real cost of doing the
            re-shell in this order, it is stated here rather than discovered later,
            and `get_workforce_status` is untouched and still feeding the desks. */}
      </div>

      {/* №53 (D-CR14) — THE FOOT: the date block, then ONE profile row.
          The three-block foot (switcher + ghost buttons + avatar) is deleted.

          ORDER NOTE — the approved mockup (`profile-and-settings.html`, frame 1)
          draws the profile row BELOW the date block. D-CR14's prose says "above".
          The mockup is the later artefact and is what David approved, so it wins
          (D-CR10: the approved mockup is the reference each rung is built against).
          The delta is recorded in the findings rather than silently resolved.

          The date stays because it is the only thing on the rail that says which
          day "Close the day" is about. It is rendered from the client clock and is
          not a live-updating timer — a ticking second hand in the corner of the
          rail is motion with nothing behind it. */}
      <div className="shellnav__foot">
        <div className="shellclock">
          <b>{new Date().toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", hour12: false })}</b>
          <span>{new Date().toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long" })}</span>
        </div>
        <ShellProfileMenu onOpenPalette={onOpenPalette} />
      </div>
    </nav>
  );
}
